import { Button, Form, Input, Select, Typography } from 'antd';
import type { ForwardRefRenderFunction } from 'react';
import { forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react';

import { tryit } from 'radash';
import type { BaseFormExposeRef, BaseFormProps } from '../../../types';
import type { ExtractionRangeEnum } from './const';
import { extractionRangeOptions } from './const';
import { FieldItem } from './filed-item';

export interface FormType {
  extraction_range: keyof typeof ExtractionRangeEnum;
  pages: string;
  ignore_headers: boolean;
  rules: ({
    specify_regional_config: 0 | 1;
    specify_regional_status: 0 | 1;
    tag_code: string;
  } | null)[];
}

export type SealFormProps = BaseFormProps<FormType>;

export interface ReturnType {
  form: FormType;
}

const PanelForm: ForwardRefRenderFunction<
  BaseFormExposeRef<ReturnType>,
  SealFormProps
> = ({ initialValue, readonly, node_id }, ref) => {
  const [form] = Form.useForm<FormType>();
  const [activeField,setActiveField] = useState<number>()
  useImperativeHandle(ref, () => ({
    assembleForm: async () => {
      const [err, values] = await tryit(form.validateFields)();
      if (err) return;

      return {
        form: values,
      };
    },
  }));

  useEffect(() => {
    form.setFieldsValue(
      initialValue || {
        extraction_range: 'ALL',
        rules: [{ specify_regional_config: 0 }],
      },
    );
  }, [initialValue, form]);

  return (
    <div className="px-6">
      <div className="flex justify-center items-center w-full">
        <Form
          disabled={readonly}
          layout="vertical"
          form={form}
          name="semantic-form"
          className="w-full"
          autoComplete="off"
        >
          <div className="flex items-end gap-4">
            <Form.Item<FormType>
              label="抽取范围"
              name="extraction_range"
              className="basis-1/3"
            >
              <Select
                getPopupContainer={(el) => el.parentElement}
                placeholder="请选择抽取范围"
                options={extractionRangeOptions}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={() => true}>
              {(form) => {
                const range: FormType['extraction_range'] =
                  form.getFieldValue('extraction_range');
                if (range !== 'TARGET') return null;
                return (
                  <Form.Item<FormType>
                    name="pages"
                    label=""
                    className="basis-2/3"
                    rules={[
                      {
                        pattern: /^((\d+)|(\d+);|(\d+)-(\d+);?)+$/,
                        message: '请输入正确的格式如 1;3-5;7',
                        warningOnly: true,
                      },
                    ]}
                  >
                    <Input placeholder="请输入页号如 1;3-5;7" maxLength={32} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>

          <Form.List name="rules">
            {(fields, operations, meta) => (
              <>
                {fields?.map((field) => (
                  <FieldItem
                    key={field.key}
                    prefix={[field.name]}
                    fullPrefix={['rules', field.name]}
                    operation={operations}
                    node_id={node_id}
                    readonly={readonly}
                    field={field}
                    activeField={activeField??0}
                    setActiveField={setActiveField}
                  />
                ))}
                <Button
                  type="dashed"
                  onClick={() => operations.add({ specify_regional_config: 0 })}
                  block
                >
                  + 添加字段和规则
                </Button>
              </>
            )}
          </Form.List>

          {/* <Form.Item noStyle shouldUpdate>
            {() => (
              <Typography>
                <pre>{JSON.stringify(form.getFieldsValue(), null, 2)}</pre>
              </Typography>
            )}
          </Form.Item> */}
        </Form>
      </div>
    </div>
  );
};

export default memo(forwardRef(PanelForm));
